import { useCallback, useEffect, useState } from "react";
import type {
  EnvironmentId,
  RuntimeDiagnosticsResponse,
  ServerTraceDiagnosticsResult,
} from "@cmmd-forge/contracts";

import {
  getPrimaryEnvironmentConnection,
  readEnvironmentConnection,
} from "../../environments/runtime";
import {
  buildPrimaryFetchInit,
  resolvePrimaryEnvironmentHttpUrl,
} from "../../environments/primary";

/**
 * Everything Forge knows first-hand about the server serving this page.
 *
 * Two sources, deliberately independent. The inventory comes over HTTP from
 * `/api/admin/runtime/diagnostics` (processes, tool checks, local services);
 * the traces come over the RPC socket. Either can fail without taking the
 * other down, and the panel says which one did — a single combined "failed to
 * load" would hide that the process table is fine and only tracing is not.
 */
export interface LocalServerDiagnostics {
  readonly inventory: RuntimeDiagnosticsResponse | undefined;
  readonly inventoryError: string | undefined;
  readonly traces: ServerTraceDiagnosticsResult | undefined;
  readonly tracesError: string | undefined;
  readonly pending: boolean;
  readonly probed: boolean;
  readonly refresh: () => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function fetchInventory(): Promise<RuntimeDiagnosticsResponse> {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/admin/runtime/diagnostics"),
    buildPrimaryFetchInit({ credentials: "include" }),
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { readonly error?: string };
    throw new Error(body.error ?? `Runtime inventory is unavailable (${response.status}).`);
  }
  return (await response.json()) as RuntimeDiagnosticsResponse;
}

export function useLocalServerDiagnostics(enabled: boolean): LocalServerDiagnostics {
  const [state, setState] = useState<{
    readonly inventory: RuntimeDiagnosticsResponse | undefined;
    readonly inventoryError: string | undefined;
    readonly traces: ServerTraceDiagnosticsResult | undefined;
    readonly tracesError: string | undefined;
    readonly pending: boolean;
  }>({
    inventory: undefined,
    inventoryError: undefined,
    traces: undefined,
    tracesError: undefined,
    pending: false,
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // Opening Diagnostics is a Passive Read. Inventory is Recheck only.
    if (!enabled || reloadToken === 0) return;
    let cancelled = false;
    setState((previous) => ({ ...previous, pending: true }));

    // allSettled, not all: one source failing must not blank the other.
    void Promise.allSettled([
      fetchInventory(),
      getPrimaryEnvironmentConnection().client.server.getTraceDiagnostics({}),
    ]).then(([inventoryResult, tracesResult]) => {
      if (cancelled) return;
      setState({
        inventory: inventoryResult.status === "fulfilled" ? inventoryResult.value : undefined,
        inventoryError:
          inventoryResult.status === "rejected" ? errorMessage(inventoryResult.reason) : undefined,
        traces: tracesResult.status === "fulfilled" ? tracesResult.value : undefined,
        tracesError:
          tracesResult.status === "rejected" ? errorMessage(tracesResult.reason) : undefined,
        pending: false,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);
  return { ...state, probed: reloadToken > 0, refresh };
}

export interface RuntimeTraceDiagnostics {
  readonly result: ServerTraceDiagnosticsResult | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
  /** Set when no read was attempted, with the reason it could not be. */
  readonly unsupportedReason: string | undefined;
  readonly refresh: () => void;
}

const NOT_CONNECTED_REASON =
  "This runtime has no open Forge connection, so its trace file cannot be read. Open a session on it and recheck.";

/**
 * The same trace read as above, taken against a chosen environment.
 *
 * `server.getTraceDiagnostics` is relay-remotable at `settings.read`, so a
 * Cloud VM can answer it over its own socket exactly as the local server does
 * over the primary one — it was only ever the caller that was hardcoded to
 * `getPrimaryEnvironmentConnection()`. That is why the Cloud VM target showed
 * control-plane metadata and no failures while the sprite was writing a trace
 * file the whole time.
 */
export function useRuntimeTraceDiagnostics(
  environmentId: EnvironmentId | null,
): RuntimeTraceDiagnostics {
  const [state, setState] = useState<{
    readonly result: ServerTraceDiagnosticsResult | undefined;
    readonly error: string | undefined;
    readonly pending: boolean;
    readonly unsupportedReason: string | undefined;
  }>({ result: undefined, error: undefined, pending: false, unsupportedReason: undefined });
  const [reloadToken, setReloadToken] = useState(0);
  const [probedEnvironmentId, setProbedEnvironmentId] = useState<EnvironmentId | null>(null);

  useEffect(() => {
    // Selection is a Passive Read. Traces are Recheck only.
    if (environmentId === null || probedEnvironmentId !== environmentId) {
      setState({
        result: undefined,
        error: undefined,
        pending: false,
        unsupportedReason: undefined,
      });
      return;
    }
    const connection = readEnvironmentConnection(environmentId);
    if (!connection) {
      // A suspended sprite is the common case here, not a fault: Sprites parks
      // a runtime after ten idle minutes and the socket goes with it.
      setState({
        result: undefined,
        error: undefined,
        pending: false,
        unsupportedReason: NOT_CONNECTED_REASON,
      });
      return;
    }

    let cancelled = false;
    setState((previous) => ({ ...previous, pending: true, unsupportedReason: undefined }));
    void connection.client.server
      .getTraceDiagnostics({})
      .then((result) => {
        if (cancelled) return;
        setState({
          result,
          error: undefined,
          pending: false,
          unsupportedReason: undefined,
        });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setState({
          result: undefined,
          error: errorMessage(cause),
          pending: false,
          unsupportedReason: undefined,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [environmentId, probedEnvironmentId, reloadToken]);

  const refresh = useCallback(() => {
    setProbedEnvironmentId(environmentId);
    setReloadToken((token) => token + 1);
  }, [environmentId]);
  return { ...state, refresh };
}
